import axios, { type AxiosError } from 'axios';

import { optimizeGlbInWorker } from './helpers/optimize-glb-in-worker';

export type PresentationLineItemPayload = {
	sku: string;
	description: string;
	category: string;
	qty: number;
	each: number;
	total: number;
	note?: string;
	kind?: 'product' | 'shipping' | 'promo';
	badge?: 'FREE' | 'PROMOTION';
	priceText?: string;
	priceNote?: string;
};

export type PresentationPromoBreakdownEntry = {
	label: string;
	amount: number;
};

export type PresentationAssemblyPayload = {
	feet: number;
	cost: number;
	skus: Array<string>;
};

export type PresentationPricingPayload = {
	islands: number;
	countertops: number;
	appliances: number;
	accessories: number;
	backsplashes: number;
	shipping: number;
	promotions: number;
	subtotal: number;
	subtotalBeforeDiscounts?: number;
	tax: number;
	taxRate: number;
	taxState?: string;
	taxNote?: string;
	taxRateLabel?: string;
	total: number;
	lineItems?: Array<PresentationLineItemPayload>;
	promoBreakdown?: Array<PresentationPromoBreakdownEntry>;
	assembly?: PresentationAssemblyPayload;
};

export type PresentationDesignDetailsPayload = {
	islandSize: string;
	islandFinish: string;
	countertop: string;
	appliances: Array<string>;
};

export type UploadPhase = 'glb' | 'main' | 'overhead' | 'estimate';

export type UploadPresentationDesignParams = {
	presentationId: string;
	designId: string;
	designName?: string;
	/** Only its tax jurisdiction is sent — the presentation recomputes every price live from the saved design. */
	pricing?: PresentationPricingPayload;
	details?: PresentationDesignDetailsPayload;
	/** GLB ArrayBuffer from headless capture export — uploads FIRST (largest payload, earliest on the wire) */
	glb?: ArrayBuffer | null;
	/** Resolves with the estimate PDF; gates only its own binary PUT at the chain tail */
	estimatePdf: Promise<Blob | null>;
	/** Resolves with the 2D overhead data URL (png or webp); gates only the overhead PUT */
	overheadPng: Promise<string>;
	/** Fires as each upload-chain milestone settles (skipped stages fire immediately) — drives progress UI */
	onPhase?: (phase: UploadPhase) => void;
	getAccessTokenSilently: () => Promise<string>;
};

const blobToBase64 = async (blob: Blob): Promise<string> => {
	const buffer = await blob.arrayBuffer();
	return new Promise<string>((resolve, reject) => {
		const reader = new FileReader();
		reader.onload = () => {
			const dataUrl = reader.result as string;
			resolve(dataUrl.split(',')[1] ?? '');
		};
		reader.onerror = () => reject(reader.error ?? new Error('FileReader error'));
		reader.readAsDataURL(new Blob([buffer]));
	});
};

const PNG_DATA_URL_PREFIX = 'data:image/png;base64,';
const WEBP_DATA_URL_PREFIX = 'data:image/webp;base64,';

const dataUrlToBase64 = (dataUrl: string): string | undefined => {
	if (!dataUrl) return undefined;
	return dataUrl.startsWith(PNG_DATA_URL_PREFIX) ? dataUrl.slice(PNG_DATA_URL_PREFIX.length) : undefined;
};

const computeSha256Hex = async (buffer: ArrayBuffer): Promise<string> => {
	const hashBuffer = await crypto.subtle.digest('SHA-256', buffer);
	return Array.from(new Uint8Array(hashBuffer))
		.map((b) => b.toString(16).padStart(2, '0'))
		.join('');
};

const RETRY_DELAYS_MS = [1000, 3000, 9000];

const sleep = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

const axiosErrorMessage = (err: unknown): string => {
	const axErr = err as AxiosError<{ message?: string }>;
	const status = axErr.response?.status;
	const serverMsg = axErr.response?.data?.message;
	if (status && serverMsg) return `HTTP ${status}: ${serverMsg}`;
	if (status) return `HTTP ${status}`;
	return axErr.message ?? String(err);
};

const uploadGlbBinary = async (params: {
	apiUrl: string;
	presentationId: string;
	designId: string;
	glb: ArrayBuffer;
	token: string;
}): Promise<string | null> => {
	const { apiUrl, presentationId, designId, glb, token } = params;

	// Draco-compress off the main thread (same recipe the server applies) — 10-20x fewer
	// transit bytes on slow uplinks. Falls back to the raw export when the worker fails.
	const optimized = await optimizeGlbInWorker(glb);
	const body = optimized ?? glb;

	const checksum = await computeSha256Hex(body);
	const glbUrl = `${apiUrl}/presentations/${presentationId}/designs/${designId}/glb`;

	// Raw exports carry uncompressed geometry buffers that gzip well; draco output barely
	// compresses, so only ship gzip when it actually shrinks the body.
	const gzStream = new Blob([body]).stream().pipeThrough(new CompressionStream('gzip'));
	const gzBody = await new Response(gzStream).arrayBuffer();
	const useGzip = gzBody.byteLength < body.byteLength * 0.97;

	let lastError: string | null = null;
	for (let attempt = 0; attempt <= RETRY_DELAYS_MS.length; attempt++) {
		try {
			await axios.put(glbUrl, useGzip ? gzBody : body, {
				headers: {
					Authorization: `Bearer ${token}`,
					'Content-Type': 'application/octet-stream',
					'x-glb-checksum': checksum,
					...(useGzip ? { 'x-body-gzip': '1' } : {}),
					...(optimized ? { 'x-glb-optimized': '1' } : {}),
				},
			});
			return null;
		} catch (err) {
			const status = (err as AxiosError).response?.status;
			// 404/405 means the api doesn't support the binary endpoint yet — fall back
			if (status === 404 || status === 405) {
				return 'FALLBACK';
			}
			lastError = axiosErrorMessage(err);
			if (attempt < RETRY_DELAYS_MS.length) {
				await sleep(RETRY_DELAYS_MS[attempt]);
			}
		}
	}
	return lastError ?? 'GLB upload failed';
};

export const uploadPresentationDesign = async (params: UploadPresentationDesignParams): Promise<[string | null, string | null]> => {
	const { presentationId, designId, designName, pricing, details, glb, estimatePdf, overheadPng, onPhase, getAccessTokenSilently } = params;

	try {
		const token = await getAccessTokenSilently();
		const apiUrl = import.meta.env.VITE_API_URL;

		// 1. Binary GLB FIRST — the largest payload starts on the wire immediately.
		// The main PUT merges into views.json server-side, so ordering no longer matters.
		let fallbackGlbBase64: string | undefined;
		if (glb) {
			const glbResult = await uploadGlbBinary({ apiUrl, presentationId, designId, glb, token });
			if (glbResult === 'FALLBACK') {
				// Older api without the binary route: attach base64 GLB to the main PUT below
				fallbackGlbBase64 = await blobToBase64(new Blob([glb]));
			} else if (glbResult !== null) {
				return [null, glbResult];
			} else {
				onPhase?.('glb');
			}
		} else {
			onPhase?.('glb');
		}

		// 2. Main design PUT — metadata only; no blueprint snapshot and no frozen prices leave the tool
		let mainResult: string | null = null;
		let lastError: string = `Failed to upload design ${designId}`;
		let mainOk = false;
		const mainBody = {
			designName,
			...(pricing ? { taxRate: pricing.taxRate, ...(pricing.taxState ? { taxState: pricing.taxState } : {}) } : {}),
			details,
			...(fallbackGlbBase64 ? { glbBase64: fallbackGlbBase64 } : {})
		};
		for (let attempt = 0; attempt <= RETRY_DELAYS_MS.length; attempt++) {
			try {
				const response = await axios.put(
					`${apiUrl}/presentations/${presentationId}/designs/${designId}`,
					mainBody,
					{ headers: { Authorization: `Bearer ${token}` } }
				);
				mainResult = response.data;
				mainOk = true;
				break;
			} catch (err) {
				lastError = axiosErrorMessage(err);
				if (attempt < RETRY_DELAYS_MS.length) {
					await sleep(RETRY_DELAYS_MS[attempt]);
				}
			}
		}
		if (!mainOk) return [null, lastError];
		if (fallbackGlbBase64) onPhase?.('glb');
		onPhase?.('main');

		// 3. Binary overhead image (raw bytes, no base64 inflation) once its capture settles.
		// Overhead before estimate: it settles first, and the manifest RMWs must not race.
		const overhead = await overheadPng;
		const isWebpOverhead = overhead.startsWith(WEBP_DATA_URL_PREFIX);
		if (overhead && (isWebpOverhead || overhead.startsWith(PNG_DATA_URL_PREFIX))) {
			try {
				const prefix = isWebpOverhead ? WEBP_DATA_URL_PREFIX : PNG_DATA_URL_PREFIX;
				const raw = Uint8Array.from(atob(overhead.slice(prefix.length)), (c) => c.charCodeAt(0));
				await axios.put(
					`${apiUrl}/presentations/${presentationId}/designs/${designId}/overhead`,
					raw.buffer,
					{ headers: { Authorization: `Bearer ${token}`, 'Content-Type': isWebpOverhead ? 'image/webp' : 'image/png' } }
				);
			} catch {
				// Legacy api without the overhead route: re-PUT main body including the base64 overhead
				try {
					const overheadPngBase64 = dataUrlToBase64(overhead);
					await axios.put(
						`${apiUrl}/presentations/${presentationId}/designs/${designId}`,
						{ ...mainBody, overheadPngBase64 },
						{ headers: { Authorization: `Bearer ${token}` } }
					);
				} catch {
					// overhead becomes best-effort at this point; design data is already stored
				}
			}
		}
		onPhase?.('overhead');

		// 4. Binary estimate PDF once its render settles
		const estimateBlob = await estimatePdf;
		if (estimateBlob) {
			try {
				const raw = await estimateBlob.arrayBuffer();
				await axios.put(
					`${apiUrl}/presentations/${presentationId}/designs/${designId}/estimate`,
					raw,
					{ headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/pdf' } }
				);
			} catch {
				// Legacy api without the estimate route: re-PUT main body including the base64 estimate
				try {
					const estimateBase64 = await blobToBase64(estimateBlob);
					await axios.put(
						`${apiUrl}/presentations/${presentationId}/designs/${designId}`,
						{ ...mainBody, estimateBase64 },
						{ headers: { Authorization: `Bearer ${token}` } }
					);
				} catch {
					// estimate becomes best-effort at this point; design data is already stored
				}
			}
		}
		onPhase?.('estimate');

		return [mainResult, null];
	} catch (err) {
		const message = err instanceof Error ? err.message : String(err);
		return [null, message];
	}
};
